import React, { useState, useEffect } from "react";
import { 
  FiClock, 
  FiAlertCircle, 
  FiHome,
  FiCheckCircle,
  FiRefreshCw
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

const PendingPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // Extract payment details from URL
  const paymentDetails = {
    amount: queryParams.get("amt") || "0.00",
    transactionId: queryParams.get("ref") || "N/A",
    orderId: queryParams.get("orderId") || queryParams.get("ref") || "N/A",
    email: queryParams.get("email") || "N/A",
    name: queryParams.get("name") || "Customer",
    examType: queryParams.get("examType") || "",
    status: queryParams.get("status") || "pending",
    error: queryParams.get("error") || null
  };

  const [isChecking, setIsChecking] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const [autoRedirect, setAutoRedirect] = useState(true);

  // Function to check payment status
  const checkPaymentStatus = async () => {
    if (!paymentDetails.orderId || paymentDetails.orderId === "N/A") {
      toast.error("No order reference available");
      return;
    }

    setIsChecking(true);
    try {
      const response = await axios.post(
        'https://pastq.eddcodesgh.com/qApi/client/expresspay-verification.php',
        {
          transactionId: paymentDetails.transactionId,
          orderId: paymentDetails.orderId,
          amount: paymentDetails.amount,
          email: paymentDetails.email,
          name: paymentDetails.name,
          status: paymentDetails.status
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 'success') {
        toast.success("Payment confirmed! Redirecting to download page...");
        
        // Store success data for download page
        localStorage.setItem('last_successful_payment', JSON.stringify({
          transactionId: response.data.transaction_id || paymentDetails.transactionId,
          orderId: paymentDetails.orderId,
          amount: response.data.amount || paymentDetails.amount,
          email: response.data.email || paymentDetails.email,
          timestamp: new Date().toISOString()
        }));
        
        // Clear any pending cart
        if (paymentDetails.examType) {
          localStorage.removeItem(`pending_cart_${paymentDetails.examType}`);
        }
        
        // Redirect to success page
        setTimeout(() => {
          navigate(`/questions/${paymentDetails.examType || ''}/payment-success`, {
            state: {
              transactionId: response.data.transaction_id || paymentDetails.transactionId,
              orderId: paymentDetails.orderId,
              amount: response.data.amount || paymentDetails.amount,
              email: response.data.email || paymentDetails.email
            }
          });
        }, 1500);
        
      } else if (response.data.status === 'failed') {
        toast.error("Payment failed. Please try again.");
        setTimeout(() => {
          navigate(`/questions/${paymentDetails.examType || ''}/payment-failed`, {
            state: {
              orderId: paymentDetails.orderId,
              amount: paymentDetails.amount,
              email: paymentDetails.email,
              reason: response.data.message || "Payment failed"
            }
          });
        }, 1500);
      } else {
        // Still pending
        toast.info("Payment is still pending. We'll check again shortly.");
        setCheckCount(prev => prev + 1);
      }
    } catch (error) {
      console.error("Status check error:", error);
      toast.error("Unable to verify payment status. Please try again later.");
    } finally {
      setIsChecking(false);
    }
  };

  // Auto-check payment status every 30 seconds
  useEffect(() => {
    if (!autoRedirect) return;

    const checkInterval = setInterval(() => {
      if (checkCount < 5) { // Check up to 5 times (2.5 minutes)
        checkPaymentStatus();
      } else {
        clearInterval(checkInterval);
        toast.info("Automatic checks stopped. You can check manually.");
        setAutoRedirect(false);
      }
    }, 30000); // Check every 30 seconds

    // Initial check after 10 seconds
    const initialCheck = setTimeout(() => {
      checkPaymentStatus();
    }, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(initialCheck);
    };
  }, [autoRedirect, checkCount]);

  // Clear URL parameters to prevent refresh issues
  useEffect(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center p-5">
              {/* Pending Icon */}
              <div 
                className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ width: "100px", height: "100px" }}
              >
                <FiClock className="text-warning" size={48} />
              </div>

              {/* Header */}
              <h2 className="mb-3 fw-bold text-warning">Payment Processing</h2>
              <p className="lead text-muted mb-4">
                Your payment of <span className="fw-bold text-primary">GHS {paymentDetails.amount}</span> is being processed.
                <br />
                This usually takes 1-2 minutes. Please wait while we confirm your payment.
              </p>

              {/* Payment Details */}
              <div className="bg-light p-4 rounded-3 mb-4">
                <div className="row">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <p className="mb-1 text-muted">Payment Amount</p>
                    <p className="fw-bold text-primary">GHS {paymentDetails.amount}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1 text-muted">Order Reference</p>
                    <p className="fw-bold">
                      <small className="text-truncate d-block" style={{ maxWidth: "200px", margin: "0 auto" }}>
                        {paymentDetails.orderId}
                      </small>
                    </p>
                  </div>
                </div>
                
                {paymentDetails.email && paymentDetails.email !== "N/A" && (
                  <div className="row mt-3">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <p className="mb-1 text-muted">Email Address</p>
                      <p className="fw-bold">
                        <small>{paymentDetails.email}</small>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1 text-muted">Customer Name</p>
                      <p className="fw-bold">
                        <small>{paymentDetails.name}</small>
                      </p>
                    </div>
                  </div>
                )}
                
                {paymentDetails.examType && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <p className="mb-1 text-muted">Exam Type</p>
                      <p className="fw-bold text-capitalize">
                        {paymentDetails.examType.replace("-", " ")}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Status Info */}
              <div className="alert alert-warning mb-4">
                <div className="d-flex align-items-center">
                  <FiAlertCircle className="me-2 flex-shrink-0" />
                  <div>
                    <strong>Important:</strong> 
                    <ul className="mb-0 mt-2 small">
                      <li>Do not refresh or close this page</li>
                      <li>Keep this tab open until payment is confirmed</li>
                      <li>You will be redirected automatically when payment is successful</li>
                      <li>Check your email for payment confirmation</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <small className="text-muted">Checking payment status...</small>
                  <small className="text-muted">{checkCount}/5 checks</small>
                </div>
                <div className="progress" style={{ height: "8px" }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated bg-warning" 
                    style={{ width: `${Math.min(checkCount * 20, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mb-4">
                <button 
                  className="btn btn-warning px-4 py-2 d-flex align-items-center justify-content-center"
                  onClick={checkPaymentStatus}
                  disabled={isChecking}
                >
                  {isChecking ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Checking...</span>
                      </div>
                      Checking...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw className="me-2" />
                      Check Status Now
                    </>
                  )}
                </button>
                
                <Link 
                  to="/questions" 
                  className="btn btn-outline-secondary px-4 py-2 d-flex align-items-center justify-content-center"
                >
                  <FiHome className="me-2" />
                  Browse More Questions
                </Link>
              </div>

              {/* Auto-check Toggle */}
              <div className="form-check form-switch d-inline-flex align-items-center mb-3">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id="autoCheckToggle"
                  checked={autoRedirect}
                  onChange={(e) => setAutoRedirect(e.target.checked)}
                />
                <label className="form-check-label small text-muted" htmlFor="autoCheckToggle">
                  Automatically check payment status
                </label>
              </div>

              {/* Support Information */}
              <div className="mt-4 small text-muted border-top pt-4">
                <p className="mb-2">
                  <strong>Need help?</strong>
                </p>
                <p className="mb-1">
                  Email: <a href="mailto:support@pastq.eddcodesgh.com">support@pastq.eddcodesgh.com</a>
                </p>
                <p className="mb-1">
                  Phone: <a href="tel:+233123456789">+233 12 345 6789</a>
                </p>
                <p className="mb-0">
                  Please have your order reference <code>{paymentDetails.orderId}</code> ready when contacting support.
                </p>
              </div>

              {/* What Happens Next */}
              <div className="mt-4">
                <h6 className="mb-3 text-muted">What happens next?</h6>
                <div className="row text-start">
                  <div className="col-md-4 mb-3">
                    <div className="d-flex">
                      <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                        <FiClock className="text-primary" />
                      </div>
                      <div>
                        <small className="fw-bold d-block">1. Payment Processing</small>
                        <small className="text-muted">ExpressPay processes your payment</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex">
                      <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                        <FiCheckCircle className="text-primary" />
                      </div>
                      <div>
                        <small className="fw-bold d-block">2. Payment Confirmation</small>
                        <small className="text-muted">We verify the payment with ExpressPay</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex">
                      <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                        <FiHome className="text-primary" />
                      </div>
                      <div>
                        <small className="fw-bold d-block">3. Access Granted</small>
                        <small className="text-muted">You'll be redirected to download your papers</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .progress-bar-animated {
          animation: progress-bar-stripes 1s linear infinite;
        }
        
        @keyframes progress-bar-stripes {
          0% { background-position: 1rem 0; }
          100% { background-position: 0 0; }
        }
        
        .card {
          border-radius: 15px;
          overflow: hidden;
        }
        
        .bg-opacity-10 {
          background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .card-body {
            padding: 2rem !important;
          }
          
          .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PendingPayment;